import { lazy, Suspense, useState } from "react";
import toast from "react-hot-toast";
import { Link, NavLink } from "react-router-dom";
import { ShimmerButton } from "./magicui/shimmer-button";
import { useAuth } from "../contexts/user-context";

const AccountMenu = lazy(() => import("./AccountMenu"));

const navigationLinkClass =
	"relative inline-flex min-h-11 items-center rounded-md px-1 text-sm outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-[#D19DFF] focus-visible:ring-offset-4 focus-visible:ring-offset-[#121113]";

const getNavigationLinkClass = (isActive: boolean) =>
	`${navigationLinkClass} ${
		isActive
			? "text-[#D19DFF] after:absolute after:inset-x-1 after:bottom-1 after:h-px after:bg-[#D19DFF]/70"
			: "text-white/60 hover:text-white"
	}`;

function Navbar() {
	const { beginDiscordSignIn, user, loading } = useAuth();
	const [signInPending, setSignInPending] = useState(false);

	const discordLogin = async () => {
		if (signInPending) return;
		setSignInPending(true);
		try {
			window.location.assign(await beginDiscordSignIn());
		} catch {
			setSignInPending(false);
			toast.error("ไม่สามารถเริ่มเข้าสู่ระบบได้ กรุณาลองอีกครั้ง");
		}
	};

	return (
		<header className="sticky left-0 top-0 z-50 w-full">
			<div className="border-b border-white/10 bg-[#121113]/88 px-4 backdrop-blur-xl">
				<div className="container-wrapper flex min-h-[4.5rem] items-center justify-between gap-4">
					<div className="flex min-w-0 items-center gap-8">
						<Link
							to="/"
							aria-label="Errorism Store home"
							className="flex min-h-11 shrink-0 items-center gap-2 rounded-md outline-none transition-opacity duration-150 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#D19DFF] focus-visible:ring-offset-4 focus-visible:ring-offset-[#121113]"
						>
							<img src="/img/logo/main.svg" className="w-6" alt="" height="236" width="172" />
							<img src="/img/logo/sss.svg" className="w-27" alt="" height="18" width="154" />
						</Link>

						<nav aria-label="เมนูหลัก" className="hidden items-center gap-7 lg:flex">
							<NavLink to="/product" className={({ isActive }) => getNavigationLinkClass(isActive)}>
								สินค้า
							</NavLink>

							{user ? (
								<>
									<NavLink to="/resource" className={({ isActive }) => getNavigationLinkClass(isActive)}>
										สคริปต์ของฉัน
									</NavLink>
									<NavLink to="/add-funds" className={({ isActive }) => getNavigationLinkClass(isActive)}>
										เติมเงิน
									</NavLink>
								</>
							) : (
								<a
									href="https://docs.errorism.co/"
									target="_blank"
									rel="noreferrer"
									className={`${navigationLinkClass} text-white/60 hover:text-white`}
								>
									คู่มือการใช้งาน
								</a>
							)}

							<a
								href="https://cfx.errorism.co/discord"
								target="_blank"
								rel="noreferrer"
								className={`${navigationLinkClass} text-white/60 hover:text-white`}
							>
								ช่องทางติดต่อ
							</a>
						</nav>
					</div>

					{loading ? (
						<div
							role="status"
							aria-live="polite"
							className="min-h-11 w-32 animate-pulse rounded-full border border-white/10 bg-white/5 motion-reduce:animate-none sm:w-36"
						>
							<span className="sr-only">กำลังตรวจสอบสถานะผู้ใช้</span>
						</div>
					) : user ? (
						<Suspense fallback={<div className="h-11 w-40 animate-pulse rounded-lg bg-white/5 motion-reduce:animate-none" />}>
							<AccountMenu />
						</Suspense>
					) : (
						<ShimmerButton
								type="button"
								onClick={() => void discordLogin()}
								disabled={signInPending}
								aria-label="เข้าสู่ระบบด้วย Discord"
								aria-busy={signInPending}
							background="#252525"
							className="min-h-11 px-6 py-2 font-medium !text-white sm:px-8"
						>
								{signInPending ? "กำลังเชื่อมต่อ…" : "เข้าสู่ระบบ"}
						</ShimmerButton>
					)}
				</div>
			</div>
		</header>
	);
}

export default Navbar;
